import { getSecureToken } from "./utils/secureTokenStorage";
import { Hola } from "./interfaces";

const API_URL = process.env.REACT_APP_API_URL || "";

// Keep this switch false until the backend accepts Bearer authorization.
export const USE_BEARER_AUTH = false;

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

interface RequestOptions extends RequestInit {
  authenticated?: boolean;
  retries?: number;
  retryDelay?: number;
}

const getAuthorizationHeader = (): string | null => {
  const token = getSecureToken("accessToken");
  if (!token) {
    return null;
  }

  return USE_BEARER_AUTH ? `Bearer ${token}` : token;
};

async function getResponseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    authenticated = false,
    retries = 0,
    retryDelay = 2000,
    headers,
    ...fetchOptions
  } = options;
  const requestHeaders = new Headers(headers);

  if (authenticated) {
    const authorization = getAuthorizationHeader();
    if (authorization) {
      requestHeaders.set("Authorization", authorization);
    }
  }

  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    requestHeaders.set("Content-Type", "application/json");
  }

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(`${API_URL}${path}`, {
        ...fetchOptions,
        headers: requestHeaders,
      });

      if (response.ok) {
        return getResponseBody<T>(response);
      }

      if (response.status >= 500 && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }

      let message = response.statusText || "API request failed";
      try {
        const errorBody = await getResponseBody<{ error?: string; message?: string }>(response);
        message = errorBody?.error || errorBody?.message || message;
      } catch {
        // Keep the HTTP status message when the error response is not JSON.
      }
      throw new ApiError(response.status, message);
    } catch (error) {
      if (attempt < retries && !(error instanceof ApiError && error.status < 500)) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }
      throw error;
    }
  }

  throw new Error("API request failed");
}

export interface SongsResponse {
  data: Hola[];
}

export interface CommentResponse {
  id?: number;
  name?: string;
  description?: string;
  lied_id?: number;
  audio_id?: string;
}

export const getSongs = (page: number, limit = 100): Promise<SongsResponse> =>
  request<SongsResponse>(`/lied?limit=${limit}&offset=${page * limit}`, {
    method: "GET",
    retries: 2,
  });

export const getCommentsBySong = (songId: number | string): Promise<CommentResponse[]> =>
  request<CommentResponse[]>(`/kommentare/${songId}`, {
    method: "GET",
  });

export const getCommentById = (commentId: string): Promise<CommentResponse[]> =>
  request<CommentResponse[]>(`/kommentarebyid/${commentId}`, {
    method: "GET",
  });

export const getSongById = (songId: string): Promise<Hola[]> =>
  request<Hola[]>(`/liedbyid/${songId}`, {
    method: "GET",
  });

export const createSong = (song: FormData): Promise<unknown> =>
  request("/lied/", {
    method: "POST",
    body: song,
  });

export const createComment = (payload: Record<string, unknown>): Promise<unknown> =>
  request("/kommentare", {
    method: "POST",
    body: JSON.stringify(payload),
    authenticated: true,
  });

export const updateComment = (
  commentId: string,
  payload: Record<string, unknown>
): Promise<unknown> =>
  request(`/kommentare/${commentId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
    authenticated: true,
  });

export const createSongImage = (
  songId: string,
  image: FormData
): Promise<unknown> =>
  request(`/imageCreate/${songId}`, {
    method: "PUT",
    body: image,
    authenticated: true,
  });

export const deleteSongImage = (
  songId: string,
  payload: Record<string, unknown>
): Promise<unknown> =>
  request(`/image/${songId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
    authenticated: true,
  });

export const createSongAudio = (
  songId: string,
  audio: FormData
): Promise<unknown> =>
  request(`/audioCreate/${songId}`, {
    method: "PUT",
    body: audio,
    authenticated: true,
  });

export const deleteSongAudio = (
  songId: string,
  payload: Record<string, unknown>
): Promise<unknown> =>
  request(`/audio/${songId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
    authenticated: true,
  });

export const deleteComment = (commentId: string): Promise<unknown> =>
  request(`/kommentare/${commentId}`, {
    method: "DELETE",
    authenticated: true,
  });

export const updateSong = (songId: string, song: FormData): Promise<unknown> =>
  request(`/lied/${songId}`, {
    method: "PUT",
    body: song,
    authenticated: true,
  });

export const deleteCommentsBySong = (songId: string): Promise<unknown> =>
  request(`/kommentarebylied/${songId}`, {
    method: "DELETE",
    authenticated: true,
  });

export const deleteSong = (songId: string): Promise<unknown> =>
  request(`/lied/${songId}`, {
    method: "DELETE",
    authenticated: true,
  });